import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/shared/auth/AuthProvider'
import { PERM } from '@/shared/auth/types'
import { Alert } from '@/shared/ui/primitives'
import { relativeTime } from '@/shared/format'
import { Icon } from '@/app/icons'
import { TopBar } from '@/app/TopBar'
import { visibleModules } from '@/app/modules'
import { approvalKeys, fetchInbox } from '@/features/billing/approval/api'
import type { InboxEntry } from '@/features/billing/approval/types'
import { fetchPortalSummary, portalKeys, type PortalSummary } from './api'
import { ApprovalQueueCard } from './home/ApprovalQueueCard'
import { GREETING, money, waitedFor } from './home/display'
import { FocusPanel, Hero, HERO_BUTTON } from './home/Hero'
import { ModuleCard, NoAccessCard, SearchTip, Shortcuts, SIDE_COLUMN_MAX_MODULES } from './home/ModulePanels'
import { Tiles, type TileSpec } from './home/Tiles'
import { useJakartaNow } from './home/ui'
import { PipelineCard, RecentCard, ReturnedCard } from './home/WorkCards'

/**
 * Beranda: the hub, not a second dashboard.
 *
 * Its job is to say what is waiting for this person, put the most urgent
 * thing within one click, and then get out of the way -- so every number on
 * it is a link to the list it counts, and nothing here answers a question
 * Monitoring answers better. No sidebar: this is not a module screen (see
 * docs/konvensi-ui.md). The approved design is the mockup linked there.
 */
export default function LandingPage() {
  const { user, can } = useAuth()
  const { now, tod } = useJakartaNow()
  const modules = visibleModules(can)

  const hasBilling = can(PERM.PORTAL_BILLING)
  const canApprove = hasBilling && can(PERM.BILLING_APPROVAL)
  const canDraft = hasBilling && can(PERM.BILLING_PROFORMA)
  const canAdd = hasBilling && can(PERM.BILLING_PROFORMA, 'add')

  const summary = useQuery({
    queryKey: portalKeys.summary,
    queryFn: fetchPortalSummary,
    // Read on arrival and when the tab comes back into focus; anything faster
    // would be a poll nobody asked for.
    staleTime: 60_000,
    enabled: hasBilling,
  })
  const inbox = useQuery({
    queryKey: approvalKeys.inbox,
    queryFn: fetchInbox,
    staleTime: 60_000,
    enabled: canApprove,
  })

  const data = summary.data
  const waiting = oldestFirst(inbox.data ?? [])
  const firstName = user?.name?.split(' ')[0] ?? ''
  const wideModules = modules.length > SIDE_COLUMN_MAX_MODULES || !hasBilling

  const today = new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  }).format(now)
  const clock = new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Jakarta',
  }).format(now)

  return (
    <>
      {/* The same top bar as every module, so the switcher and Ctrl K are
          where people will look for them later. */}
      <TopBar />

      <Hero
        tod={tod}
        eyebrow={
          <>
            {today} · <span className="tabular-nums">{clock}</span> WIB
          </>
        }
        title={
          <>
            {GREETING[tod]},{' '}
            <em className="bg-linear-to-r from-white to-[#d6f0ff] bg-clip-text not-italic text-transparent">{firstName}</em>
          </>
        }
        summary={heroSummary({ modules: modules.length, roleName: user?.roleName, hasBilling, canApprove, data, waiting, now })}
        actions={heroActions({ canApprove, canDraft, canAdd, waiting: waiting.length, returned: data?.myReturned ?? 0 })}
        focus={hasBilling ? focusPanel({ data, waiting, canApprove, now, loading: summary.isPending || (canApprove && inbox.isPending) }) : null}
      />

      <main className="mx-auto max-w-6xl px-4 pb-14 sm:px-6">
        {modules.length === 0 ? (
          <NoAccessCard name={user?.name} roleName={user?.roleName} />
        ) : (
          <>
            {hasBilling && <Tiles tiles={tiles(data, waiting, now)} />}
            {summary.isError && (
              <div className="mt-6">
                <Alert>Ringkasan belum bisa dimuat. Muat ulang halaman, atau buka menunya langsung dari Billing.</Alert>
              </div>
            )}

            {wideModules && (
              <section className="mt-7" aria-label="Modul">
                <h2 className="mb-3 ml-0.5 text-[0.8rem] font-bold tracking-[0.09em] text-gray-500 uppercase">Modul</h2>
                <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(13rem,1fr))]">
                  {modules.map((module) => (
                    <ModuleCard key={module.id} module={module} />
                  ))}
                </div>
              </section>
            )}

            {hasBilling && (
              <div className="mt-7 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
                <div className="grid min-w-0 gap-6">
                  {canApprove && <ApprovalQueueCard entries={waiting} now={now} />}
                  {canDraft && data && (data.returned.length > 0 || !canApprove) && (
                    <ReturnedCard documents={data.returned} total={data.myReturned ?? data.returned.length} />
                  )}
                  {data?.pipeline && <PipelineCard pipeline={data.pipeline} />}
                  {canDraft && data && <RecentCard documents={data.recent} />}
                </div>
                <aside className="grid min-w-0 gap-6">
                  {!wideModules && (
                    <section aria-label="Modul">
                      <h2 className="mb-3 ml-0.5 text-[0.8rem] font-bold tracking-[0.09em] text-gray-500 uppercase">Modul</h2>
                      <div className="grid gap-4">
                        {modules.map((module) => (
                          <ModuleCard key={module.id} module={module} />
                        ))}
                      </div>
                    </section>
                  )}
                  <Shortcuts />
                  <SearchTip />
                </aside>
              </div>
            )}
          </>
        )}
      </main>
    </>
  )
}

/** Oldest wait first: the one most likely to breach its deadline. */
function oldestFirst(entries: InboxEntry[]): InboxEntry[] {
  const time = (entry: InboxEntry) => (entry.waitingSince ? Date.parse(entry.waitingSince) : Number.POSITIVE_INFINITY)
  return [...entries].sort((a, b) => time(a) - time(b))
}

/** One sentence that says what today holds, instead of a bare title. */
function heroSummary({
  modules,
  roleName,
  hasBilling,
  canApprove,
  data,
  waiting,
  now,
}: {
  modules: number
  roleName?: string
  hasBilling: boolean
  canApprove: boolean
  data?: PortalSummary
  waiting: InboxEntry[]
  now: Date
}): ReactNode {
  if (modules === 0) {
    return (
      <>
        Peran <b>{roleName}</b> belum diberi akses ke modul mana pun. Hubungi administrator untuk membuka akses.
      </>
    )
  }
  if (!hasBilling) return 'Modul yang bisa Anda buka ada di bawah.'
  if (!data) return 'Menyiapkan ringkasan kerja Anda…'

  const oldest = waiting[0]
  if (canApprove && oldest) {
    return (
      <>
        Ada <b>{waiting.length} proforma</b> menunggu persetujuan Anda. Yang paling lama sudah menunggu{' '}
        <b>{waitedFor(oldest.waitingSince, now)}</b>.
      </>
    )
  }
  const returned = data.myReturned ?? 0
  const inReview = data.myInReview ?? 0
  if (returned > 0) {
    return (
      <>
        <b>{returned} proforma</b> dikembalikan untuk diperbaiki
        {inReview > 0 ? `, dan ${inReview} lainnya sedang di tahap persetujuan.` : '.'}
      </>
    )
  }
  if (inReview > 0) {
    return (
      <>
        {inReview} proforma Anda sedang di tahap persetujuan. <b>Tidak ada yang menunggu tindakan Anda.</b>
      </>
    )
  }
  return (
    <>
      Tidak ada yang menunggu Anda hari ini. <b>Semua antrian bersih.</b>
    </>
  )
}

function heroActions({
  canApprove,
  canDraft,
  canAdd,
  waiting,
  returned,
}: {
  canApprove: boolean
  canDraft: boolean
  canAdd: boolean
  waiting: number
  returned: number
}): ReactNode {
  const actions: ReactNode[] = []
  if (canApprove && waiting > 0) {
    actions.push(
      <Link key="approve" to="/billing/approvals" className={HERO_BUTTON.white}>
        <Icon name="check" className="size-4.5" /> Tinjau persetujuan
      </Link>,
    )
  } else if (canDraft && returned > 0) {
    actions.push(
      <Link key="fix" to="/billing/proforma?status=RETURNED" className={HERO_BUTTON.white}>
        <Icon name="undo" className="size-4.5" /> Perbaiki yang dikembalikan
      </Link>,
    )
  }
  if (canAdd) {
    actions.push(
      <Link key="new" to="/billing/proforma/new" className={actions.length ? HERO_BUTTON.ghost : HERO_BUTTON.white}>
        <Icon name="plus" className="size-4.5" /> Proforma Baru
      </Link>,
    )
  }
  return actions
}

/** The single most urgent thing, one click away. */
function focusPanel({
  data,
  waiting,
  canApprove,
  now,
  loading,
}: {
  data?: PortalSummary
  waiting: InboxEntry[]
  canApprove: boolean
  now: Date
  loading: boolean
}): ReactNode {
  if (loading || !data) return null

  const oldest = waiting[0]
  if (canApprove && oldest) {
    return (
      <FocusPanel label="Paling lama menunggu Anda" icon={<Icon name="flag" className="size-3.5" />}>
        <div className="mt-3 font-mono text-[0.95rem] font-semibold text-white">{oldest.docNo}</div>
        <div className="mt-0.5 text-white/80">{oldest.customerName}</div>
        <div className="mt-2.5 text-2xl font-bold tracking-tight text-white tabular-nums">
          {money(oldest.currency, oldest.totalAmt)}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Pill hot={oldest.overdue}>
            <Icon name="clock" className="size-3.5" /> {waitedFor(oldest.waitingSince, now)} menunggu
          </Pill>
          <Pill>
            Level {oldest.levelNo} dari {oldest.totalLevels}
          </Pill>
          {oldest.asDelegate && <Pill>Sebagai delegasi</Pill>}
        </div>
        <Link to={`/billing/proforma/${oldest.id}`} className={`${HERO_BUTTON.white} mt-4 w-full justify-center`}>
          Tinjau sekarang <Icon name="arrowRight" className="size-4" />
        </Link>
      </FocusPanel>
    )
  }

  const returned = data.returned[0]
  if (returned) {
    return (
      <FocusPanel label="Dikembalikan ke Anda" icon={<Icon name="undo" className="size-3.5" />}>
        <div className="mt-3 font-mono text-[0.95rem] font-semibold text-white">{returned.docNo ?? 'Tanpa nomor'}</div>
        <div className="mt-0.5 text-white/80">
          {returned.customerName ?? '—'} · {money(returned.currency, returned.totalAmt)}
        </div>
        <p className="mt-3 rounded-lg bg-black/15 px-3 py-2.5 text-[0.85rem] text-white/90">
          {returned.note ? `“${returned.note}”` : 'Alasan pengembalian tidak tercatat.'}
          {(returned.returnedBy || returned.returnedAt) && (
            <small className="mt-1 block text-white/70">
              {[returned.returnedBy, returned.returnedAt && relativeTime(returned.returnedAt)].filter(Boolean).join(', ')}
            </small>
          )}
        </p>
        <Link to={`/billing/proforma/${returned.id}`} className={`${HERO_BUTTON.white} mt-4 w-full justify-center`}>
          Perbaiki sekarang <Icon name="arrowRight" className="size-4" />
        </Link>
      </FocusPanel>
    )
  }

  return (
    <FocusPanel label="Semua beres" icon={<Icon name="check" className="size-3.5" />}>
      <div className="mt-3 flex items-center gap-3.5">
        <span className="grid size-13 shrink-0 place-items-center rounded-full bg-[#7dffb4]/20 text-[#aaffcf]">
          <Icon name="check" className="size-6" />
        </span>
        <p className="text-white/85">
          <b className="block text-lg font-bold text-white">Tidak ada pekerjaan tertunda</b>
          Buat proforma baru saat tagihan berikutnya siap.
        </p>
      </div>
    </FocusPanel>
  )
}

function Pill({ hot = false, children }: { hot?: boolean; children: ReactNode }) {
  return (
    <span
      className={
        hot
          ? 'inline-flex items-center gap-1.5 rounded-full bg-[#ffc700]/95 px-2.5 py-0.5 text-xs font-semibold text-[#3b2a00]'
          : 'inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium text-white'
      }
    >
      {children}
    </span>
  )
}

/**
 * The four counts, each with one line of context when the data can back it.
 * A tile the user has no permission for is absent from the summary and so
 * absent here, rather than shown as a misleading zero.
 */
function tiles(data: PortalSummary | undefined, waiting: InboxEntry[], now: Date): TileSpec[] {
  if (!data) return []
  const result: TileSpec[] = []

  if (data.waitingOnMe !== undefined && data.waitingOnMe !== null) {
    const overdue = waiting.filter((entry) => entry.overdue).length
    const oldest = waiting[0]
    result.push({
      to: '/billing/approvals',
      label: 'Menunggu persetujuan Anda',
      value: data.waitingOnMe,
      icon: 'check',
      tone: 'accent',
      micro:
        overdue > 0
          ? { text: `${overdue} lewat batas waktu`, icon: 'clock', warn: true }
          : oldest
            ? { text: `Terlama ${waitedFor(oldest.waitingSince, now)}`, icon: 'clock' }
            : undefined,
    })
  }

  if (data.myDrafts !== undefined && data.myDrafts !== null) {
    const lastDraft = data.recent.find((doc) => doc.status === 'DRAFT')
    result.push({
      to: '/billing/proforma?status=DRAFT',
      label: 'Draft saya',
      value: data.myDrafts,
      icon: 'document',
      micro: lastDraft?.updatedAt ? { text: `Terakhir diubah ${relativeTime(lastDraft.updatedAt, now)}`, icon: 'pen' } : undefined,
    })
  }

  if (data.myInReview !== undefined && data.myInReview !== null) {
    result.push({
      to: '/billing/proforma?status=PENDING_APPROVAL',
      label: 'Sedang diproses',
      value: data.myInReview,
      icon: 'clipboard',
    })
  }

  if (data.myReturned !== undefined && data.myReturned !== null) {
    const latest = data.returned[0]
    result.push({
      to: '/billing/proforma?status=RETURNED',
      label: 'Perlu diperbaiki',
      value: data.myReturned,
      icon: 'undo',
      tone: 'danger',
      micro:
        latest?.returnedAt
          ? {
              text: [latest.returnedBy && `Dari ${latest.returnedBy}`, relativeTime(latest.returnedAt, now)].filter(Boolean).join(', '),
              icon: 'flag',
            }
          : undefined,
    })
  }
  return result
}
